// src/AssessmentStepperV2.js
// Three-level vertical stepper: major step ▸ risk-area ▸ risk-source
// v0.3 — 2025-07-22 - Fixed alignment and progress bar placement

import React, { useState } from 'react';
import {
  Drawer, Box, Text, ActionIcon, Stack, Group,
  useMantineTheme, ScrollArea, Progress, Collapse, Tooltip
} from '@mantine/core';
import {
  IconMenu2, IconCheck, IconCircle, IconCircleFilled, IconMinus,
} from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { useSelector } from 'react-redux';

function toTitleCase(str = '') {
  if (!str) return '';
  
  // Special words that should maintain specific capitalization
  const specialWords = {
    'ai': 'AI',
    'api': 'API',
    'ui': 'UI',
    'ux': 'UX',
    'id': 'ID',
    'url': 'URL',
    'http': 'HTTP',
    'https': 'HTTPS',
    'json': 'JSON',
    'xml': 'XML',
    'sql': 'SQL',
    'css': 'CSS',
    'html': 'HTML',
    'pdf': 'PDF',
    'csv': 'CSV'
  };
  
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]/g, ' ')
    .split(/\s+/)
    .map(word => {
      const lowerWord = word.toLowerCase();
      
      // Handle "and" → "&"
      if (lowerWord === 'and') {
        return '&';
      }
      
      // Handle special words (AI, API, etc.)
      if (specialWords[lowerWord]) {
        return specialWords[lowerWord];
      }
      
      // Standard title case
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

function MinimalStepIcon({ active, completed, isSkipped, isDeferred, notSelected, size = 14 }) {
  const theme = useMantineTheme();

  // Completed state - green circle with check
  if (completed) {
    return (
      <Box
        sx={{
          position: 'relative', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: size, 
          height: size, 
          backgroundColor: theme.colors.green[6],
          borderRadius: '50%',
        }}
      >
        <IconCheck size={size - 4} color={theme.white} />
      </Box>
    );
  }
  
  // Active state - blue filled circle
  if (active) {
    return <IconCircleFilled size={size} color={theme.colors.blue[6]} />;
  }
  
  // Deferred state (saved for later) - yellow circle with clock icon
  if (isDeferred) {
    return (
      <Box
        sx={{
          position: 'relative', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: size, 
          height: size, 
          backgroundColor: theme.colors.yellow[5],
          borderRadius: '50%',
        }}
      >
        <IconCircle size={size - 6} color={theme.white} stroke={2} />
      </Box>
    );
  }
  
  // Skipped state (section marked as N/A) - show minus icon
  if (isSkipped) {
    return <IconMinus size={size} color={theme.colors.gray[5]} />;
  }
  
  // Not selected state (available but not chosen) - show empty circle
  if (notSelected) {
    return <IconCircle size={size} color={theme.colors.gray[4]} stroke={1} />;
  }
  
  // Default inactive state - empty circle outline
  return <IconCircle size={size} color={theme.colors.gray[4]} />;
}

export default function AssessmentStepperV2({
  modelId,
  modelName,
  steps,                 // [{label, sections:[riskAreas]}]
  activeStep, setActiveStep,
  activeSectionIndex, setActiveSectionIndex,
  activeSourceName,      // currently active source name in risk flow
  //activeSourceIndex,     // currently active source index in risk flow
}) {
  const theme = useMantineTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [expandedSources, setExpandedSources] = useState({}); // Track expanded areas
  const [stepperWidth, setStepperWidth] = useState(300); // Resizable width
  const riskFormData = useSelector(
    s => s.risks.models[modelId]?.riskFormData || {}
  );

  const SAFETY_IDX = steps.findIndex(s => s.label === 'safetyRisks');
  
  // Better completion detection - check both progress and if risks are saved
  const isSrcDone = (src, areaName) => {
    const hasProgress = (src.progress || 0) >= 100;
    const sectionRisks = riskFormData[areaName]?.sectionRisks?.[src.name] || [];
    const hasSavedRisk = sectionRisks.some(risk => risk.status === 'Saved');
    return hasProgress && hasSavedRisk;
  };
  
  const isAreaDone = (area) => {
    const allSrcs = riskFormData[area]?.riskSources || [];
    // Filter out "Other" sources for completion calculation
    const srcs = allSrcs.filter(s => s.name !== 'Other');
    const sel = srcs.filter(x => x.selected);
    return sel.length && sel.every(src => isSrcDone(src, area));
  };

  const drawerContent = (
    <Box 
      sx={{ 
        width: isMobile ? '100%' : stepperWidth, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        backgroundColor: theme.white,
        position: 'relative'
      }}
    >
      {/* Header */}
      <Box p="md" style={{ borderBottom: `1px solid ${theme.colors.gray[2]}` }}>
        <Text 
          size="lg" 
          weight={600} 
          color={theme.colors.dark[8]}
          mb="xs"
        >
          {toTitleCase(modelName)}
        </Text>
        <Text 
          size="sm" 
          c={theme.colors.gray[6]}
        >
          Assessment Progress
        </Text>
      </Box>
      
      {/* Resize Handle */}
      {!isMobile && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: -3,
            width: 6,
            height: '100%',
            cursor: 'ew-resize',
            backgroundColor: theme.colors.gray[1],
            borderRight: `1px solid ${theme.colors.gray[3]}`,
            '&:hover': {
              backgroundColor: theme.colors.blue[2],
              borderRight: `2px solid ${theme.colors.blue[5]}`,
            },
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseDown={(e) => {
            const startX = e.clientX;
            const startWidth = stepperWidth;
            
            const handleMouseMove = (e) => {
              const newWidth = Math.max(250, Math.min(500, startWidth + (e.clientX - startX)));
              setStepperWidth(newWidth);
            };
            
            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };
            
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        >
          <Box sx={{
            width: 2,
            height: 20,
            backgroundColor: theme.colors.gray[4],
            borderRadius: theme.radius.xs,
          }} />
        </Box>
      )}

      {/* Content */}
      <ScrollArea p="md" style={{ flex: 1 }}>
        <Stack spacing="lg">
          {steps.map((step, stepIdx) => {
            // major completion:
            const done = stepIdx===SAFETY_IDX
              ? step.sections.every(isAreaDone)
              : activeStep>stepIdx;

            return (
              <Stack key={step.label} spacing="sm">
                {/* Major Step */}
                <Group
                  spacing="sm"
                  onClick={() => setActiveStep(stepIdx)}
                  sx={{
                    cursor: 'pointer',
                    padding: theme.spacing.sm,
                    borderRadius: theme.radius.sm,
                    '&:hover': {
                      backgroundColor: theme.colors.gray[0],
                    },
                  }}
                >
                  <MinimalStepIcon 
                    completed={done} 
                    active={activeStep===stepIdx}
                    isSkipped={false}
                  />
                  <Text 
                    weight={activeStep===stepIdx ? 600 : 500}
                    sx={{
    color: activeStep === stepIdx ? theme.colors.blue[6] : theme.colors.dark[7],
  }}
                  >
                    {toTitleCase(step.label)}
                  </Text>
                </Group>

                {/* Sections */}
                <Stack spacing="xs" ml="xl">
                  {step.sections.map((area, areaIdx) => {
                    const allSrcs = riskFormData[area]?.riskSources || [];
                    // trim potential leading / trailing whitespace in raw keys
                    const fullAreaName   = toTitleCase(area.trim());
                    const needsTruncArea = fullAreaName.length > 28;
                    const areaDisplay    = needsTruncArea
                      ? fullAreaName.substring(0, 25) + '…'
                      : fullAreaName;
                    // Filter out "Other" sources
                    const srcs = allSrcs.filter(s => s.name !== 'Other');
                    const sel = srcs.filter(s => s.selected);
                    // Check area status - distinguish between deferred and not applicable
                    const areaStatus = riskFormData[area]?.status;
                    const isDeferred = areaStatus === 'DEFERRED';
                    const isNotApplicable = areaStatus === 'NA';
                    const sectionDone = isAreaDone(area);
                    const isActiveArea = activeStep === stepIdx && activeSectionIndex === areaIdx;
                    const areaKey = `${stepIdx}-${areaIdx}`;
                    const isExpanded = expandedSources[areaKey] || isActiveArea;

                    return (
                      <Stack key={area} spacing="xs">
                        {/* Section - Fixed alignment structure */}
                        <Box
                          onClick={() => {
                            setActiveStep(stepIdx);
                            setActiveSectionIndex(areaIdx);
                            // Toggle expansion on click
                            setExpandedSources(prev => ({
                              ...prev,
                              [areaKey]: !prev[areaKey]
                            }));
                          }}
                          sx={{
                            cursor: 'pointer',
                            padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
                            borderRadius: theme.radius.sm,
                            '&:hover': {
                              backgroundColor: theme.colors.gray[0],
                            },
                          }}
                        >
                          <Group spacing="sm" align="flex-start" noWrap>
                            {/* Icon with fixed width container */}
                            <Box sx={{ width: 12, flexShrink: 0, pt: 2 }}>
                              <MinimalStepIcon 
                                completed={sectionDone} 
                                active={isActiveArea}
                                isSkipped={isNotApplicable}
                                isDeferred={isDeferred}
                                notSelected={false}
                                size={12}
                              />
                            </Box>
                            
                            {/* Text and progress in a column */}
                            <Stack spacing={4} sx={{ flex: 1, minWidth: 0 }}>
                              <Tooltip 
                                label={fullAreaName} 
                                withArrow 
                                disabled={!needsTruncArea}
                              >
                                <Text 
                                  size="sm"
                                  c={
                                    isNotApplicable 
                                      ? theme.colors.gray[5]
                                      : isDeferred
                                        ? theme.colors.yellow[7]
                                        : isActiveArea
                                          ? theme.colors.blue[6]
                                          : theme.colors.gray[7]
                                  }
                                  sx={{
                                    flex: 1,            // let it take remaining space
                                    minWidth: 0,        // _allow_ shrinking below intrinsic width
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {areaDisplay}
                                </Text>
                              </Tooltip>
                              
                              {/* Progress bar underneath */}
                              {sel.length > 0 && (
                                <Progress
                                  value={(sel.filter(src => isSrcDone(src, area)).length / sel.length) * 100}
                                  size="xs"
                                  color={sectionDone ? 'green' : 'blue'}
                                  radius="sm"
                                  label={`${sel.filter(src => isSrcDone(src, area)).length}/${sel.length}`}
                                  sx={{ width: '100%' }}
                                />
                              )}
                            </Stack>
                          </Group>
                        </Box>

                        {/* Sources (only on safety step) */}
                        {stepIdx === SAFETY_IDX && srcs.length > 0 && (
                          <Collapse in={isExpanded}>
                            <Stack spacing="sm" ml="xl" mt="xs">
                              {srcs
                                .slice()
                                .sort((a, b) => (a.selected === b.selected ? 0 : a.selected ? -1 : 1))
                                .map(src => {
                                  const done = isSrcDone(src, area);
                                  const isActive = activeStep === SAFETY_IDX && 
                                                 steps[SAFETY_IDX].sections[activeSectionIndex] === area &&
                                                 src.name === activeSourceName;
                                  
                                  const notSelected = !src.selected;
                                  const isSelected = src.selected;
                                  // Use area-level status for sources too
                                  const sourceSkipped = isNotApplicable;
                                  const sourceDeferred = isDeferred;

                                  const displayName = toTitleCase(src.name);
                                  const needsTruncation = displayName.length > 25;
                                  const truncatedName = needsTruncation 
                                    ? displayName.substring(0, 22) + '...' 
                                    : displayName;

                                  const sourceElement = (
                                    <Stack key={src.name} spacing={2}>
                                      <Group spacing="sm" align="center" noWrap>
                                        {/* Icon with fixed width */}
                                        <Box sx={{ width: 10, flexShrink: 0 }}>
                                          <MinimalStepIcon 
                                            completed={done} 
                                            isSkipped={sourceSkipped}
                                            isDeferred={sourceDeferred}
                                            notSelected={notSelected}
                                            active={isActive}
                                            size={10}
                                          />
                                        </Box>
                                        
                                        {/* Source name */}
                                        <Text 
                                          size="xs"
                                          c={
                                            sourceSkipped
                                              ? theme.colors.gray[5]
                                              : sourceDeferred
                                                ? theme.colors.yellow[7]
                                                : notSelected 
                                                  ? theme.colors.gray[5]
                                                  : done 
                                                    ? theme.colors.green[7]
                                                    : isActive
                                                      ? theme.colors.blue[6]
                                                      : theme.colors.gray[6]
                                          }
                                          sx={{ 
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1
                                          }}
                                        >
                                          {truncatedName}
                                        </Text>
                                      </Group>
                                      
                                      {/* Progress bar underneath for selected sources */}
                                      {isSelected && (
                                        <Box ml={22} mr="sm">
                                          <Progress
                                            value={src.progress || 0}
                                            size={3}
                                            color={done ? 'green' : 'blue'}
                                            radius="sm"
                                          />
                                        </Box>
                                      )}
                                    </Stack>
                                  );

                                  return needsTruncation ? (
                                    <Tooltip key={src.name} label={displayName} withArrow>
                                      {sourceElement}
                                    </Tooltip>
                                  ) : sourceElement;
                                })}
                            </Stack>
                          </Collapse>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </ScrollArea>
    </Box>
  );

  return isMobile ? (
    <>
      <ActionIcon
        aria-label="open nav"
        onClick={() => setDrawerOpen(o => !o)}
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1100,
          backgroundColor: theme.white,
          border: `1px solid ${theme.colors.gray[2]}`,
        }}
        size="lg"
      >
        <IconMenu2 size={18} />
      </ActionIcon>
      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        position="left"
        size="sm"
      >
        {drawerContent}
      </Drawer>
    </>
  ) : drawerContent;
}
// // src/AssessmentStepperV2.js
// // Three-level vertical stepper: major step ▸ risk-area ▸ risk-source
// // v0.2 — 2025-07-22 - Mantine version

// import React, { useState } from 'react';
// import {
//   Drawer, Box, Text, ActionIcon, Stack, Group,
//   useMantineTheme, ScrollArea, Progress, Collapse, Tooltip
// } from '@mantine/core';
// import {
//   IconMenu2, IconCheck, IconCircle, IconCircleFilled, IconMinus,
// } from '@tabler/icons-react';
// import { useMediaQuery } from '@mantine/hooks';
// import { useSelector } from 'react-redux';

// function toTitleCase(str = '') {
//   return str
//     .replace(/([a-z])([A-Z])/g, '$1 $2')
//     .replace(/[_-]/g, ' ')
//     .split(/\s+/)
//     .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
//     .join(' ');
// }

// function MinimalStepIcon({ active, completed, isSkipped, notSelected, size = 14 }) {
//   const theme = useMantineTheme();

//   // Completed state - green circle with check
//   if (completed) {
//     return (
//       <Box
//         sx={{
//           position: 'relative', 
//           display: 'flex', 
//           alignItems: 'center', 
//           justifyContent: 'center',
//           width: size, 
//           height: size, 
//           backgroundColor: theme.colors.green[6],
//           borderRadius: '50%',
//         }}
//       >
//         <IconCheck size={size - 4} color={theme.white} />
//       </Box>
//     );
//   }
  
//   // Active state - blue filled circle
//   if (active) {
//     return <IconCircleFilled size={size} color={theme.colors.blue[6]} />;
//   }
  
//   // Skipped state (section marked as N/A) - show minus icon
//   if (isSkipped) {
//     return <IconMinus size={size} color={theme.colors.gray[5]} />;
//   }
  
//   // Not selected state (available but not chosen) - show empty circle
//   if (notSelected) {
//     return <IconCircle size={size} color={theme.colors.gray[4]} stroke={1} />;
//   }
  
//   // Default inactive state - empty circle outline
//   return <IconCircle size={size} color={theme.colors.gray[4]} />;
// }

// export default function AssessmentStepperV2({
//   modelId,
//   modelName,
//   steps,                 // [{label, sections:[riskAreas]}]
//   activeStep, setActiveStep,
//   activeSectionIndex, setActiveSectionIndex,
//   activeSourceName,      // currently active source name in risk flow
//   //activeSourceIndex,     // currently active source index in risk flow
// }) {
//   const theme = useMantineTheme();
//   const isMobile = useMediaQuery('(max-width: 768px)');
//   const [drawerOpen, setDrawerOpen] = useState(true);
//   const [expandedSources, setExpandedSources] = useState({}); // Track expanded areas
//   const [stepperWidth, setStepperWidth] = useState(300); // Resizable width
//   const riskFormData = useSelector(
//     s => s.risks.models[modelId]?.riskFormData || {}
//   );

//   const SAFETY_IDX = steps.findIndex(s => s.label === 'safetyRisks');
  
//   // Better completion detection - check both progress and if risks are saved
//   const isSrcDone = (src, areaName) => {
//     const hasProgress = (src.progress || 0) >= 100;
//     const sectionRisks = riskFormData[areaName]?.sectionRisks?.[src.name] || [];
//     const hasSavedRisk = sectionRisks.some(risk => risk.status === 'Saved');
//     return hasProgress && hasSavedRisk;
//   };
  
//   const isAreaDone = (area) => {
//     const allSrcs = riskFormData[area]?.riskSources || [];
//     // Filter out "Other" sources for completion calculation
//     const srcs = allSrcs.filter(s => s.name !== 'Other');
//     const sel = srcs.filter(x => x.selected);
//     return sel.length && sel.every(src => isSrcDone(src, area));
//   };

//   const drawerContent = (
//     <Box 
//       sx={{ 
//         width: isMobile ? '100%' : stepperWidth, 
//         height: '100%', 
//         display: 'flex', 
//         flexDirection: 'column', 
//         backgroundColor: theme.white,
//         position: 'relative'
//       }}
//     >
//       {/* Header */}
//       <Box p="md" style={{ borderBottom: `1px solid ${theme.colors.gray[2]}` }}>
//         <Text 
//           size="lg" 
//           weight={600} 
//           color={theme.colors.dark[8]}
//           mb="xs"
//         >
//           {toTitleCase(modelName)}
//         </Text>
//         <Text 
//           size="sm" 
//           color={theme.colors.gray[6]}
//         >
//           Assessment Progress
//         </Text>
//       </Box>
      
//       {/* Resize Handle */}
//       {!isMobile && (
//         <Box
//           sx={{
//             position: 'absolute',
//             top: 0,
//             right: -3,
//             width: 6,
//             height: '100%',
//             cursor: 'ew-resize',
//             backgroundColor: theme.colors.gray[1],
//             borderRight: `1px solid ${theme.colors.gray[3]}`,
//             '&:hover': {
//               backgroundColor: theme.colors.blue[2],
//               borderRight: `2px solid ${theme.colors.blue[5]}`,
//             },
//             zIndex: 1000,
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center'
//           }}
//           onMouseDown={(e) => {
//             const startX = e.clientX;
//             const startWidth = stepperWidth;
            
//             const handleMouseMove = (e) => {
//               const newWidth = Math.max(250, Math.min(500, startWidth + (e.clientX - startX)));
//               setStepperWidth(newWidth);
//             };
            
//             const handleMouseUp = () => {
//               document.removeEventListener('mousemove', handleMouseMove);
//               document.removeEventListener('mouseup', handleMouseUp);
//             };
            
//             document.addEventListener('mousemove', handleMouseMove);
//             document.addEventListener('mouseup', handleMouseUp);
//           }}
//         >
//           <Box sx={{
//             width: 2,
//             height: 20,
//             backgroundColor: theme.colors.gray[4],
//             borderRadius: theme.radius.xs,
//           }} />
//         </Box>
//       )}

//       {/* Content */}
//       <ScrollArea flex={1} p="md">
//         <Stack spacing="lg">
//           {steps.map((step, stepIdx) => {
//             // major completion:
//             const done = stepIdx===SAFETY_IDX
//               ? step.sections.every(isAreaDone)
//               : activeStep>stepIdx;

//             return (
//               <Stack key={step.label} spacing="sm">
//                 {/* Major Step */}
//                 <Group
//                   spacing="sm"
//                   onClick={() => setActiveStep(stepIdx)}
//                   sx={{
//                     cursor: 'pointer',
//                     padding: theme.spacing.sm,
//                     borderRadius: theme.radius.sm,
//                     '&:hover': {
//                       backgroundColor: theme.colors.gray[0],
//                     },
//                   }}
//                 >
//                   <MinimalStepIcon 
//                     completed={done} 
//                     active={activeStep===stepIdx}
//                     isSkipped={false}
//                   />
//                   <Text 
//                     weight={activeStep===stepIdx ? 600 : 500}
//                     color={activeStep===stepIdx ? theme.colors.blue[6] : theme.colors.dark[7]}
//                   >
//                     {toTitleCase(step.label)}
//                   </Text>
//                 </Group>

//                 {/* Sections */}
//                 <Stack spacing="xs" ml="xl">
//                   {step.sections.map((area, areaIdx) => {
//                     const allSrcs = riskFormData[area]?.riskSources || [];
//                     // Filter out "Other" sources
//                     const srcs = allSrcs.filter(s => s.name !== 'Other');
//                     const sel = srcs.filter(s => s.selected);
//                     // Only truly skipped if area is marked as N/A, not just no selection
//                     const skipped = riskFormData[area]?.status === 'NA';
//                     const sectionDone = isAreaDone(area);
//                     const isActiveArea = activeStep === stepIdx && activeSectionIndex === areaIdx;
//                     const areaKey = `${stepIdx}-${areaIdx}`;
//                     const isExpanded = expandedSources[areaKey] || isActiveArea;

//                     return (
//                       <Stack key={area} spacing="xs">
//                         {/* Section */}
//                         <Group
//                           position="apart"
//                           spacing="sm"
//                           onClick={() => {
//                             setActiveStep(stepIdx);
//                             setActiveSectionIndex(areaIdx);
//                             // Toggle expansion on click
//                             setExpandedSources(prev => ({
//                               ...prev,
//                               [areaKey]: !prev[areaKey]
//                             }));
//                           }}
//                           sx={{
//                             cursor: 'pointer',
//                             padding: `${theme.spacing.xs}px ${theme.spacing.sm}px`,
//                             borderRadius: theme.radius.sm,
//                             '&:hover': {
//                               backgroundColor: theme.colors.gray[0],
//                             },
//                           }}
//                         >
//                           <Group spacing="sm" align="center">
//                             <MinimalStepIcon 
//                               completed={sectionDone} 
//                               active={isActiveArea}
//                               isSkipped={skipped}
//                               notSelected={false} // Areas are not "not selected" 
//                               size={12}
//                             />
//                             <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
//                               <Tooltip 
//                                 label={toTitleCase(area)} 
//                                 withArrow 
//                                 disabled={toTitleCase(area).length <= 25}
//                               >
//                                 <Text 
//                                   size="sm"
//                                   color={
//                                     skipped 
//                                       ? theme.colors.gray[5]
//                                       : isActiveArea
//                                         ? theme.colors.blue[6]
//                                         : theme.colors.gray[7]
//                                   }
//                                   sx={{
//                                     overflow: 'hidden',
//                                     textOverflow: 'ellipsis',
//                                     whiteSpace: 'nowrap',
//                                     maxWidth: stepperWidth - 200
//                                   }}
//                                 >
//                                   {toTitleCase(area)}
//                                 </Text>
//                               </Tooltip>
//                               {/* Progress bar instead of badge */}
//                               {sel.length > 0 && (
//                                 <Box sx={{ width: 80, ml: 'sm' }}>
//                                   <Progress
//                                     value={(sel.filter(src => isSrcDone(src, area)).length / sel.length) * 100}
//                                     size="xs"
//                                     color={sectionDone ? 'green' : 'blue'}
//                                     radius="sm"
//                                     label={`${sel.filter(src => isSrcDone(src, area)).length}/${sel.length}`}
//                                   />
//                                 </Box>
//                               )}
//                             </Box>
//                           </Group>
//                         </Group>

//                         {/* Sources (only on safety step) */}
//                         {stepIdx === SAFETY_IDX && srcs.length > 0 && (
//                           <Collapse in={isExpanded}>
//                             <Stack spacing="xs" ml="xl" mt="xs">
//                               {srcs
//                                 .slice()
//                                 .sort((a, b) => (a.selected === b.selected ? 0 : a.selected ? -1 : 1))
//                                 .map(src => {
//                                   const done = isSrcDone(src, area);
//                                   const isActive = activeStep === SAFETY_IDX && 
//                                                  steps[SAFETY_IDX].sections[activeSectionIndex] === area &&
//                                                  src.name === activeSourceName;
                                  
//                                   // Better logic for source states
//                                   const notSelected = !src.selected;
//                                   const isSelected = src.selected;
//                                   // Only truly skipped if the section itself is marked as N/A
//                                   const isSkipped = skipped; // Use section-level skip, not source level

//                                   const displayName = toTitleCase(src.name);
//                                   const needsTruncation = displayName.length > 20;
//                                   const truncatedName = needsTruncation 
//                                     ? displayName.substring(0, 17) + '...' 
//                                     : displayName;

//                                   const sourceElement = (
//                                     <Group key={src.name} spacing="sm" align="center">
//                                       <MinimalStepIcon 
//                                         completed={done} 
//                                         isSkipped={isSkipped}
//                                         notSelected={notSelected}
//                                         active={isActive}
//                                         size={10}
//                                       />
//                                       <Box 
//                                         sx={{ 
//                                           flex: 1,
//                                           maxWidth: stepperWidth - 120,
//                                           position: 'relative',
//                                           display: 'flex',
//                                           alignItems: 'center'
//                                         }}
//                                       >
//                                         {/* Background Progress Bar */}
//                                         {isSelected && (
//                                           <Progress
//                                             value={src.progress || 0}
//                                             size="xs"
//                                             color={done ? 'green' : 'blue'}
//                                             radius="sm"
//                                             sx={{
//                                               position: 'absolute',
//                                               top: '50%',
//                                               left: 0,
//                                               right: 0,
//                                               transform: 'translateY(-50%)',
//                                               opacity: 0.3,
//                                             }}
//                                           />
//                                         )}
                                        
//                                         {/* Text on top of progress */}
//                                         <Text 
//                                           size="xs"
//                                           color={
//                                             isSkipped
//                                               ? theme.colors.gray[5]
//                                               : notSelected 
//                                                 ? theme.colors.gray[5]
//                                                 : done 
//                                                   ? theme.colors.green[7]
//                                                   : isActive
//                                                     ? theme.colors.blue[6]
//                                                     : theme.colors.gray[6]
//                                           }
//                                           sx={{ 
//                                             position: 'relative',
//                                             zIndex: 1,
//                                             backgroundColor: 'rgba(255,255,255,0.8)',
//                                             paddingX: 2,
//                                             borderRadius: theme.radius.xs,
//                                             overflow: 'hidden',
//                                             textOverflow: 'ellipsis',
//                                             whiteSpace: 'nowrap',
//                                             width: '100%'
//                                           }}
//                                         >
//                                           {truncatedName}
//                                         </Text>
//                                       </Box>
//                                     </Group>
//                                   );

//                                   return needsTruncation ? (
//                                     <Tooltip key={src.name} label={displayName} withArrow>
//                                       {sourceElement}
//                                     </Tooltip>
//                                   ) : sourceElement;
//                                 })}
//                             </Stack>
//                           </Collapse>
//                         )}
//                       </Stack>
//                     );
//                   })}
//                 </Stack>
//               </Stack>
//             );
//           })}
//         </Stack>
//       </ScrollArea>
//     </Box>
//   );

//   return isMobile ? (
//     <>
//       <ActionIcon
//         aria-label="open nav"
//         onClick={() => setDrawerOpen(o => !o)}
//         sx={{
//           position: 'fixed',
//           top: 16,
//           left: 16,
//           zIndex: 1100,
//           backgroundColor: theme.white,
//           border: `1px solid ${theme.colors.gray[2]}`,
//         }}
//         size="lg"
//       >
//         <IconMenu2 size={18} />
//       </ActionIcon>
//       <Drawer
//         opened={drawerOpen}
//         onClose={() => setDrawerOpen(false)}
//         position="left"
//         size="sm"
//       >
//         {drawerContent}
//       </Drawer>
//     </>
//   ) : drawerContent;
// }